import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { TrendingUp, Loader2, CheckCircle, AlertCircle, Lock, ArrowLeft } from 'lucide-react';
import { ThemeToggle } from '../components/ThemeToggle';
import PageSEO from '../components/PageSEO';
import axios from 'axios';


export default function ClientIntake() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: URL, 2: Contact Info, 3: Loading, 4: Success
  const [url, setUrl] = useState('');
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    company: '',
    industry: '',
    customIndustry: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null);

  // Validate and normalize URL
  const normalizeUrl = (inputUrl) => {
    let cleanUrl = inputUrl.trim();
    if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      cleanUrl = 'https://' + cleanUrl;
    }
    return cleanUrl;
  };

  // Step 1: Handle URL submission
  const handleUrlSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!url.trim()) {
      setError('Please enter a website URL');
      return;
    }

    const cleanUrl = normalizeUrl(url);

    // Strict URL validation using regex
    const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i;
    if (!urlRegex.test(cleanUrl)) {
      setError('Please enter a valid website URL (e.g., example.com)');
      return;
    }

    // Basic URL validation
    try {
      new URL(cleanUrl);
    } catch (err) {
      setError('Please enter a valid website URL (e.g., example.com)');
      return;
    }

    setStep(2); // Move to contact info
  };

  // Step 2: Handle contact info submission
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!contactInfo.name || !contactInfo.email) {
      setError('Please enter your name and email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactInfo.email)) {
      setError('Please enter a valid email address (missing @ or .)');
      return;
    }

    setLoading(true);
    setStep(3); // Move to loading

    try {
      const cleanUrl = normalizeUrl(url);

      // Call backend to scan website and create report
      const response = await axios.post('/public/website-scan', {
        url: cleanUrl,
        clientName: contactInfo.name,
        clientEmail: contactInfo.email,
        clientCompany: contactInfo.company,
        clientIndustry: contactInfo.industry === 'Other' ? contactInfo.customIndustry : contactInfo.industry
      });

      setReportData(response.data);
      setStep(4); // Move to success/report
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        'Failed to scan website. Please check the URL and try again.'
      );
      setStep(2); // Back to contact info
      setLoading(false);
    }
  };

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://aisearchadvisers.com/#organization",
        "name": "AI Search Advisers",
        "url": "https://aisearchadvisers.com/",
        "description": "AI Search Advisers helps businesses assess and improve website readiness for AI-driven discovery through website audits, AI visibility insights, crawlability analysis, structured data evaluation, and prioritized recommendations.",
        "email": "info@aisearchadvisers.com"
      },
      {
        "@type": "WebSite",
        "@id": "https://aisearchadvisers.com/#website",
        "url": "https://aisearchadvisers.com/",
        "name": "AI Search Advisers",
        "publisher": { "@id": "https://aisearchadvisers.com/#organization" },
        "inLanguage": "en-US"
      },
      {
        "@type": "WebPage",
        "@id": "https://aisearchadvisers.com/scan#webpage",
        "url": "https://aisearchadvisers.com/scan",
        "name": "Free AI Search Visibility Audit | AI Search Advisers",
        "description": "Run a free AI search visibility audit to uncover crawlability, content, structured data, and other opportunities that may improve AI discoverability.",
        "isPartOf": { "@id": "https://aisearchadvisers.com/#website" },
        "about": { "@id": "https://aisearchadvisers.com/#organization" },
        "publisher": { "@id": "https://aisearchadvisers.com/#organization" },
        "inLanguage": "en-US"
      },
      {
        "@type": "Service",
        "name": "Free AI Search Readiness Audit",
        "serviceType": "AI Search Visibility Audit",
        "provider": { "@id": "https://aisearchadvisers.com/#organization" },
        "url": "https://aisearchadvisers.com/scan",
        "description": "A comprehensive evaluation of technical and content factors affecting AI search discoverability.",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "url": "https://aisearchadvisers.com/scan"
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-background dark:to-slate-900 transition-colors relative">
      <PageSEO 
        title="Free AI Search Visibility Audit | AI Search Advisers"
        description="Run a free AI search visibility audit to uncover crawlability, content, structured data, and other opportunities that may improve AI discoverability."
        canonical="https://aisearchadvisers.com/scan"
        schema={schema}
      />
      {/* Top Navigation */}
      <div className="absolute top-6 left-6 z-10">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/')} 
          className="text-[#feab3d] hover:text-[#e8960a]"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>
      </div>

      {/* Main Content */}
      <div className="pt-24 pb-20 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-12 text-center">
            <h2 className="font-['Fraunces',serif] text-3xl sm:text-4xl md:text-5xl leading-tight font-bold text-slate-900 dark:text-white mb-4">
              {step === 1 && "See Your Website's Potential"}
              {step === 2 && 'Get Your Personalized Report'}
              {step === 3 && 'Analyzing Your Website'}
              {step === 4 && 'Your Website Intelligence Report'}
            </h2>
            <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400 mb-4">
              {step === 1 && 'Enter your website URL to get AI-powered insights. No code required.'}
              {step === 2 && 'Share your contact info to receive your detailed analysis.'}
              {step === 3 && 'Our AI is scanning your website for insights...'}
              {step === 4 && "Here's what we found about your website."}
            </p>

            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-full text-sm">
              <Lock className="w-4 h-4 text-green-600 dark:text-green-500" />
              <span className="text-green-700 dark:text-green-400">No code required • Public data only • 100% safe</span>
            </div>
          </div>

          {/* Step Indicator */}
          <div className="flex gap-2 mb-12 justify-center">
            <div className={`h-2 w-12 rounded-full ${step >= 1 ? 'bg-[#FEAB3D]' : 'bg-slate-300'}`}></div>
            <div className={`h-2 w-12 rounded-full ${step >= 2 ? 'bg-[#FEAB3D]' : 'bg-slate-300'}`}></div>
            <div className={`h-2 w-12 rounded-full ${step >= 3 ? 'bg-[#FEAB3D]' : 'bg-slate-300'}`}></div>
            <div className={`h-2 w-12 rounded-full ${step >= 4 ? 'bg-[#FEAB3D]' : 'bg-slate-300'}`}></div>
          </div>

          {/* STEP 1: URL Entry */}
          {step === 1 && (
            <div className="bg-white dark:bg-card rounded-2xl shadow-lg dark:shadow-none border border-transparent dark:border-border p-8 transition-colors">
              <form onSubmit={handleUrlSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-3">
                    Your Website URL
                  </label>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="example.com or https://example.com"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      className="h-12 text-base px-4 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                      autoFocus
                    />
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                    We'll analyze your website's public data, including SEO, content, performance, and more.
                  </p>
                </div>

                {error && (
                  <div className="flex gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-12 bg-[#FEAB3D] text-[#021426] font-semibold rounded-none hover:bg-[#e8960a]"
                >
                  Scan My Website
                </Button>

                <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
                  Your URL is secure and will only be used to generate your report.
                </p>
              </form>
            </div>
          )}

          {/* STEP 2: Contact Information */}
          {step === 2 && (
            <div className="bg-white dark:bg-card rounded-2xl shadow-lg dark:shadow-none border border-transparent dark:border-border p-8 transition-colors">
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-3">
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    placeholder="John Smith"
                    value={contactInfo.name}
                    onChange={(e) => setContactInfo({ ...contactInfo, name: e.target.value })}
                    className="h-12 text-base px-4 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-3">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    placeholder="you@company.com"
                    value={contactInfo.email}
                    onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                    className="h-12 text-base px-4 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-3">
                    Company (Optional)
                  </label>
                  <Input
                    type="text"
                    placeholder="Your Company"
                    value={contactInfo.company}
                    onChange={(e) => setContactInfo({ ...contactInfo, company: e.target.value })}
                    className="h-12 text-base px-4 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-3">
                    Industry (Optional)
                  </label>
                  <select
                    value={contactInfo.industry}
                    onChange={(e) => setContactInfo({ ...contactInfo, industry: e.target.value })}
                    className="w-full h-12 px-4 border border-slate-300 dark:border-slate-700 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-[#FEAB3D] dark:bg-slate-800 dark:text-white"
                  >
                    <option value="">Select an industry</option>
                    <option value="SaaS">SaaS</option>
                    <option value="E-commerce">E-commerce</option>
                    <option value="Publishing">Publishing</option>
                    <option value="Services">Services</option>
                    <option value="Other">Other</option>
                  </select>
                  {contactInfo.industry === 'Other' && (
                    <div className="mt-3 animate-in fade-in slide-in-from-top-2 duration-300">
                      <Input
                        type="text"
                        placeholder="Please specify your industry"
                        value={contactInfo.customIndustry}
                        onChange={(e) => setContactInfo({ ...contactInfo, customIndustry: e.target.value })}
                        className="h-12 text-base px-4 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                        required
                      />
                    </div>
                  )}
                </div>

                {error && (
                  <div className="flex gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-12 bg-[#FEAB3D] text-[#021426] font-semibold rounded-none hover:bg-[#e8960a]"
                >
                  Generate My Report
                </Button>

                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="w-full text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white font-medium text-sm transition-colors"
                >
                  ← Back
                </button>

                <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
                  We respect your privacy. Your information will only be used to deliver your insights.
                </p>
              </form>
            </div>
          )}

          {/* STEP 3: Loading */}
          {step === 3 && (
            <div className="bg-white dark:bg-card rounded-2xl shadow-lg dark:shadow-none border border-transparent dark:border-border p-12 text-center space-y-6 transition-colors">
              <Loader2 className="w-12 h-12 text-[#FEAB3D] mx-auto animate-spin" />
              <div>
                <p className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                  Scanning {url}...
                </p>
                <p className="text-slate-600 dark:text-slate-400">
                  This usually takes 30-60 seconds. We're analyzing your SEO, content, performance, and more.
                </p>
              </div>
            </div>
          )}

          {/* STEP 4: Report Success / Preview */}
          {step === 4 && reportData && (
            <div className="bg-white dark:bg-card rounded-2xl shadow-lg dark:shadow-none border border-transparent dark:border-border p-8 space-y-8 transition-colors">
              <div className="flex gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-700 dark:text-green-400">
                  Your Website Intelligence Report has been generated successfully.
                </p>
              </div>

              {/* Report Preview - Inspired by Layout 1 */}
              {(() => {
                // Compute metrics dynamically from the scan results
                const seo = reportData.seoAnalysis || {};
                const cnt = reportData.contentAnalysis || {};
                const perf = reportData.performanceMetrics || {};
                const checks = [
                  { pass: !!seo.title, critical: true },
                  { pass: !!seo.metaDescription, critical: true },
                  { pass: (seo.h1Count || 0) > 0, critical: true },
                  { pass: !!seo.hasCanonical, critical: false },
                  { pass: !!seo.hasMobileViewport, critical: true },
                  { pass: (cnt.paragraphCount || 0) > 3, critical: false },
                  { pass: (cnt.imageCount || 0) > 0, critical: false },
                  { pass: (cnt.linkCount || 0) > 0, critical: false },
                  { pass: !!cnt.hasCTA, critical: true },
                  { pass: (perf.asyncScripts || 0) > 0, critical: false },
                  { pass: (perf.scriptCount || 0) <= 15, critical: false },
                  { pass: (perf.stylesheetCount || 0) <= 5, critical: false },
                ];
                const total = checks.length;
                const good = checks.filter(c => c.pass).length;
                const critical = checks.filter(c => !c.pass && c.critical).length;
                const recommended = total - good - critical;
                const score = Math.round((good / total) * 100);
                let label = 'Needs Work';
                if (score >= 80) label = 'Excellent';
                else if (score >= 60) label = 'Very Good';
                else if (score >= 40) label = 'Fair';
                const dashOffset = 251.2 - (251.2 * score / 100);

                return (
              <div>
                <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6 text-center">Your Free SEO Report is here!</h2>

                {/* Top Bar */}
                <div className="flex flex-col sm:flex-row justify-between items-center bg-white dark:bg-card border border-slate-200 dark:border-slate-800 shadow-sm rounded-lg p-3 mb-8 gap-4">
                  <div className="flex items-center gap-3 w-full sm:w-auto overflow-hidden">
                    <div className="w-8 h-8 flex items-center justify-center bg-slate-50 dark:bg-slate-700/50 rounded shadow-sm border border-slate-200 dark:border-slate-700 shrink-0">
                      <TrendingUp className="w-5 h-5 text-[#021426] dark:text-[#FEAB3D]" />
                    </div>
                    <span className="font-medium text-slate-800 dark:text-slate-200 truncate">{url || 'https://example.com'}</span>
                  </div>
                  <div className="shrink-0 w-full sm:w-auto">
                    <Button onClick={() => navigate(`/client/report/${reportData.reportId}`)} className="w-full sm:w-auto bg-[#FEAB3D] text-[#021426] hover:bg-[#e8960a] font-semibold h-10 px-6 transition-transform hover:scale-105 shadow-md">
                      View Full Report
                    </Button>
                  </div>
                </div>

                {/* Main Score Area – DYNAMIC */}
                <div className="bg-slate-50 dark:bg-card/50 border border-slate-200 dark:border-border rounded-xl p-8 mb-8 flex flex-col md:flex-row justify-between items-center gap-8 shadow-sm">
                  <div className="max-w-md text-center md:text-left">
                    <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-4">Overall Site Score</h3>
                    <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm sm:text-base">
                      A very good score is between 60 and 80. For best results, you should strive for 70 and above.
                    </p>
                  </div>
                  {/* Circular Score Donut */}
                  <div className="relative w-40 h-40 sm:w-48 sm:h-48 flex items-center justify-center flex-shrink-0">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-slate-200 dark:text-slate-800" />
                      <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="8" fill="transparent" strokeDasharray="251.2" strokeDashoffset={dashOffset} strokeLinecap="round" className="text-[#FEAB3D]" />
                    </svg>
                    <div className="absolute flex flex-col items-center justify-center text-center">
                      <div className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white">{score}<span className="text-lg sm:text-xl text-slate-500 dark:text-slate-400 font-medium">/100</span></div>
                      <div className="text-xs sm:text-sm font-bold text-[#021426] dark:text-[#FEAB3D] mt-1">{label}</div>
                    </div>
                  </div>
                </div>

                {/* Summary Metric Cards – DYNAMIC */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <div className="bg-white dark:bg-card border border-slate-200 dark:border-border rounded-xl p-5 border-l-4 border-l-slate-700 dark:border-l-slate-500 shadow-sm flex flex-col justify-center transition-transform hover:-translate-y-1">
                    <div className="text-3xl font-bold text-slate-900 dark:text-white mb-1">{total} <span className="text-xs text-slate-500 dark:text-slate-400 font-normal">of {total}</span></div>
                    <div className="text-xs font-semibold text-slate-700 dark:text-slate-300">All Items</div>
                  </div>
                  <div className="bg-white dark:bg-card border border-slate-200 dark:border-border rounded-xl p-5 border-l-4 border-l-red-500 dark:border-l-red-500 shadow-sm flex flex-col justify-center transition-transform hover:-translate-y-1">
                    <div className="text-3xl font-bold text-red-600 dark:text-red-400 mb-1">{critical} <span className="text-xs text-slate-500 dark:text-slate-400 font-normal">of {total}</span></div>
                    <div className="text-xs font-semibold text-slate-700 dark:text-slate-300">Critical Issues</div>
                  </div>
                  <div className="bg-white dark:bg-card border border-slate-200 dark:border-border rounded-xl p-5 border-l-4 border-l-[#FEAB3D] dark:border-l-[#FEAB3D] shadow-sm flex flex-col justify-center transition-transform hover:-translate-y-1">
                    <div className="text-3xl font-bold text-[#FEAB3D] dark:text-blue-400 mb-1">{recommended} <span className="text-xs text-slate-500 dark:text-slate-400 font-normal">of {total}</span></div>
                    <div className="text-xs font-semibold text-slate-700 dark:text-slate-300">Recommended</div>
                  </div>
                  <div className="bg-white dark:bg-card border border-slate-200 dark:border-border rounded-xl p-5 border-l-4 border-l-green-500 dark:border-l-green-500 shadow-sm flex flex-col justify-center transition-transform hover:-translate-y-1">
                    <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-1">{good} <span className="text-xs text-slate-500 dark:text-slate-400 font-normal">of {total}</span></div>
                    <div className="text-xs font-semibold text-slate-700 dark:text-slate-300">Good Results</div>
                  </div>
                </div>
              </div>
                );
              })()}

              {/* Next Steps */}
              <div className="border-t dark:border-border pt-8">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">What's Next?</h3>
                <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
                  <p>
                    <span className="font-semibold">1. Review Your Report</span> - Understand your website's strengths and opportunities.
                  </p>
                  <p>
                    <span className="font-semibold">2. Download Your Data</span> - Use the "View Full Report" button to access and save your detailed analysis.
                  </p>
                  <p>
                    <span className="font-semibold">3. Discuss Next Steps</span> - Upgrade for deeper behavior analytics or our team's guidance.
                  </p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Button
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="h-12 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  Back Home
                </Button>
                <Button
                  onClick={() => navigate('/auth?tab=register')}
                  className="h-12 bg-[#FEAB3D] text-[#021426] font-semibold rounded-lg hover:bg-[#e8960a]"
                >
                  Explore Full Platform
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
